import { useCallback, useEffect, useMemo, useState } from 'react';

import { type AdminIntegrationVisibilityRow, adminClient } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate } from '@/lib/format';
import { visibilitySummary } from '@/lib/integrationVisibility';

import {
  type AskableIntegration,
  type Delegate,
  type Grant,
  type GrantPage,
  endOfDay,
  errorText,
  levelObject,
  permissionsApi,
} from './permissionsApi';
import { usePersonNames } from './names';
import { BackLink, Problem, givenBy, untilText, whoOf } from './shared';

type Row = { integration: AskableIntegration; title: string; visibility?: AdminIntegrationVisibilityRow['visibility'] };

/** The integrations of the service's manifests, named by the admin catalog when it answers. */
/** "1 delegate", "3 delegates", or "Delegates" when the service does not list them. */
export const delegateCount = (n: number | undefined) => (n === undefined ? 'Delegates' : `${n} delegate${n === 1 ? '' : 's'}`);

export function joinRows(askable: AskableIntegration[], visibility: AdminIntegrationVisibilityRow[]): Row[] {
  const byId = new Map(visibility.map((row) => [row.integrationId, row]));
  return askable
    .map((integration) => ({ integration, title: byId.get(integration.id)?.title ?? integration.id, visibility: byId.get(integration.id)?.visibility }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/** Holder counts, a few calls at a time. A count that fails stays out of the map. */
async function loadCounts(rows: Row[], into: (key: string, count: number) => void) {
  const jobs = rows.flatMap((row) => row.integration.levels.map((level) => [row.integration.id, level.id] as const));
  for (let i = 0; i < jobs.length; i += 6) {
    await Promise.all(
      jobs.slice(i, i + 6).map(async ([integration, level]) => {
        try {
          into(`${integration}.${level}`, await permissionsApi.holderCount(integration, level));
        } catch {
          // No count for this level: the list shows a dash.
        }
      }),
    );
  }
}

const COLUMNS = [{ label: 'Integration' }, { label: 'Who can use it', className: 'hidden md:table-cell' }, { label: 'Levels and holders' }, { label: 'Decides requests', className: 'text-right' }];

export function IntegrationsTab({ open, onOpen }: { open?: string; onOpen: (id?: string) => void }) {
  const [rows, setRows] = useState<Row[]>();
  const [error, setError] = useState<string>();
  const [query, setQuery] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const [askable, visibility] = await Promise.all([
        permissionsApi.askable(),
        adminClient.listIntegrationVisibility().then((out) => out.items).catch(() => [] as AdminIntegrationVisibilityRow[]),
      ]);
      const joined = joinRows(askable, visibility);
      setRows(joined);
      void loadCounts(joined, (key, count) => setCounts((now) => ({ ...now, [key]: count })));
    } catch (failure) {
      setError(errorText(failure));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (rows ?? []).filter((row) => !needle || row.title.toLowerCase().includes(needle) || row.integration.id.includes(needle));
  }, [rows, query]);

  const selected = open ? rows?.find((row) => row.integration.id === open) : undefined;
  if (open && selected) return <IntegrationDetail row={selected} onBack={() => onOpen()} />;

  return (
    <DataTable toolbar={<SearchInput value={query} onChange={setQuery} placeholder="Search name or integration id" label="Search integrations" />}>
      <DataTableHead columns={COLUMNS} />
      {error ? (
        <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
      ) : !rows ? (
        <DataTableMessage colSpan={COLUMNS.length} loading>
          Loading integrations
        </DataTableMessage>
      ) : shown.length === 0 ? (
        <DataTableMessage colSpan={COLUMNS.length}>{open ? 'This integration is not in the service manifests.' : 'No integration matches.'}</DataTableMessage>
      ) : (
        <tbody>
          {shown.map((row) => (
            <DataTableRow key={row.integration.id} to={`?tab=integrations&open=${encodeURIComponent(row.integration.id)}`}>
              <DataTableCell>
                <button type="button" className="text-left font-medium text-foreground hover:underline" onClick={() => onOpen(row.integration.id)}>
                  {row.title}
                </button>
                <div className="text-xs text-muted-foreground">{row.integration.id}</div>
              </DataTableCell>
              <DataTableCell className="hidden md:table-cell">{row.visibility ? <StatusPill tone={visibilitySummary({ visibility: row.visibility }).tone}>{visibilitySummary({ visibility: row.visibility }).label}</StatusPill> : <span className="text-muted-foreground">n/a</span>}</DataTableCell>
              <DataTableCell>
                <div className="flex flex-wrap gap-1.5">
                  {row.integration.levels.map((level) => (
                    <StatusPill key={level.id} tone={level.writes ? 'warning' : 'neutral'}>
                      {level.id} {counts[`${row.integration.id}.${level.id}`] ?? '–'}
                    </StatusPill>
                  ))}
                </div>
              </DataTableCell>
              <DataTableCell className="text-right text-xs text-muted-foreground">
                {row.integration.granter.kind === 'delegates' ? delegateCount(row.integration.granter.delegates?.length) : 'Admins'}
              </DataTableCell>
            </DataTableRow>
          ))}
        </tbody>
      )}
    </DataTable>
  );
}

function IntegrationDetail({ row, onBack }: { row: Row; onBack: () => void }) {
  const { integration } = row;
  const [version, setVersion] = useState(0);
  const refresh = () => setVersion((n) => n + 1);
  return (
    <div className="space-y-6">
      <div>
        <BackLink label="All integrations" onClick={onBack} />
        <h2 className="text-lg font-semibold">{row.title}</h2>
        <p className="text-xs text-muted-foreground">{integration.id}</p>
      </div>
      {integration.levels.map((level) => (
        <LevelHolders key={`${level.id}-${version}`} integration={integration.id} level={level.id} writes={level.writes} onChanged={refresh} />
      ))}
      <Delegates key={`d-${version}`} integration={integration.id} />
      <GiveForm integration={integration} onGiven={refresh} />
    </div>
  );
}

function LevelHolders({ integration, level, writes, onChanged }: { integration: string; level: string; writes: boolean; onChanged: () => void }) {
  const [page, setPage] = useState<GrantPage>();
  const [grants, setGrants] = useState<Grant[]>([]);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const object = levelObject(integration, level);

  const load = useCallback(
    async (after?: string | null) => {
      try {
        const next = await permissionsApi.grants('object', object, { after });
        setPage(next);
        setGrants((now) => (after ? [...now, ...next.grants] : next.grants));
        setError(undefined);
      } catch (failure) {
        setError(errorText(failure));
      }
    },
    [object],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const end = async (grant: Grant) => {
    setBusy(grant.id);
    try {
      await permissionsApi.endGrant(grant.id);
      onChanged();
    } catch (failure) {
      setError(errorText(failure));
      setBusy(undefined);
    }
  };

  return (
    <AdminSection title={`${level}${writes ? ' (can change data)' : ''}`} description={page ? `${page.total} holder${page.total === 1 ? '' : 's'}` : undefined} bodyClassName="p-0">
      {error ? <div className="p-4"><Problem message={error} onRetry={() => void load()} /></div> : null}
      {page && grants.length === 0 && !error ? <p className="p-5 text-sm text-muted-foreground">Nobody holds this level.</p> : null}
      {grants.length > 0 ? (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <tr>
              {['Person', 'Given by', 'Until', 'Reason', ''].map((label) => (
                <th key={label} scope="col" className="px-4 py-2 font-medium">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grants.map((grant) => (
              <tr key={grant.id} className="border-b border-border last:border-0">
                <td className="px-4 py-2.5">{whoOf(grant)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{givenBy(grant)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{untilText(grant.ends_at)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{grant.reason || '–'}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button type="button" size="sm" variant="outline" disabled={busy === grant.id} onClick={() => void end(grant)} aria-label={`End ${whoOf(grant)} ${level}`}>
                    End
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {page?.next ? (
        <div className="border-t border-border p-3">
          <Button type="button" size="sm" variant="outline" onClick={() => void load(page.next)}>
            Show more ({grants.length} of {page.total})
          </Button>
        </div>
      ) : null}
    </AdminSection>
  );
}

function Delegates({ integration }: { integration: string }) {
  const nameOf = usePersonNames();
  const [list, setList] = useState<Delegate[]>();
  const [error, setError] = useState<string>();
  const [email, setEmail] = useState('');
  const load = useCallback(async () => {
    try {
      setList(await permissionsApi.delegates(integration));
      setError(undefined);
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [integration]);
  useEffect(() => {
    void load();
  }, [load]);
  const act = async (job: () => Promise<void>) => {
    try {
      await job();
      setEmail('');
      await load();
    } catch (failure) {
      setError(errorText(failure));
    }
  };
  return (
    <AdminSection title="Delegates" description="A delegate decides the requests for this integration. With none, the admins decide.">
      {error ? <Problem message={error} onRetry={() => void load()} /> : null}
      {list?.length === 0 ? <p className="mb-3 text-sm text-muted-foreground">No delegates. The admins decide.</p> : null}
      <ul className="mb-3 space-y-1.5">
        {list?.map((d) => (
          <li key={d.person} className="flex items-center justify-between gap-3 text-sm">
            <span>
              {nameOf(d.person)} <span className="text-xs text-muted-foreground">added {formatDate(d.added_at)}</span>
            </span>
            <Button type="button" size="sm" variant="outline" onClick={() => void act(() => permissionsApi.removeDelegate(integration, d.person))} aria-label={`Remove delegate ${nameOf(d.person)}`}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) void act(() => permissionsApi.addDelegate(integration, email.trim()));
        }}
      >
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email of the new delegate" aria-label="New delegate email" className="max-w-xs" />
        <Button type="submit" size="sm" disabled={!email.trim()}>
          Add delegate
        </Button>
      </form>
    </AdminSection>
  );
}

function GiveForm({ integration, onGiven }: { integration: AskableIntegration; onGiven: () => void }) {
  const [person, setPerson] = useState('');
  const [level, setLevel] = useState(integration.levels[0]?.id ?? '');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const ready = person.trim() !== '' && level !== '' && reason.trim() !== '' && !busy;
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await permissionsApi.give({ person: person.trim(), integration: integration.id, level, endsAt: endOfDay(until), reason: reason.trim() });
      setPerson('');
      setReason('');
      setUntil('');
      onGiven();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AdminSection title="Give access" description="Writes the level and its operation, the same pair an approved request writes.">
      <form
        className="grid gap-3 md:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) void submit();
        }}
      >
        <label className="text-xs text-muted-foreground">
          Person
          <Input type="email" value={person} onChange={(e) => setPerson(e.target.value)} placeholder="name@rapidsos.com" className="mt-1" />
        </label>
        <label className="text-xs text-muted-foreground">
          Level
          <select value={level} onChange={(e) => setLevel(e.target.value)} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            {integration.levels.map((l) => (
              <option key={l.id} value={l.id}>
                {l.id}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Until (optional)
          <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1" />
        </label>
        <label className="text-xs text-muted-foreground">
          Reason
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this person needs it" className="mt-1" />
        </label>
        <div className="md:col-span-2">
          {error ? <div className="mb-3"><Problem message={error} /></div> : null}
          <Button type="submit" disabled={!ready}>
            {busy ? 'Giving…' : 'Give'}
          </Button>
        </div>
      </form>
    </AdminSection>
  );
}
